/**
 * NotificationBell — Header dropdown surfacing platform update notifications.
 *
 * Wires the C++ updater plumbing (FGDB_UpdateChecker + GDB_UpdateInstaller +
 * GDB_ModuleInstaller) to the React header. Data flow:
 *
 *   1. GDBCore.GetAvailableUpdates  — one-shot fetch on mount (C++ returns
 *      JSON array of available updates for the core plugin + each owned
 *      module).
 *   2. Bridge event "updates.available" — push refresh when the C++ side
 *      re-checks (e.g. on startup, timer, manual refresh).
 *   3. **Phase 17.7-05** — GDBCore.GetAvailableUpdatePlan returns a unified
 *      multi-plugin plan (FGDB_UpdatePlan from Plan 17.7-04). When non-empty
 *      and not dismissed, the bell renders ONE plan-summary row (D-08)
 *      instead of the legacy per-plugin list. Click expands the breakdown;
 *      "Apply plan" calls GDBCore.ApplyUpdatePlan; "Dismiss" stores the
 *      batchId locally + fires plan_dismissed_by_user telemetry.
 *
 * Entitlement filter: module updates (productId != "gamedevbuddy") are
 * hidden unless the user owns that module per useAuth().modules. The core
 * plugin update is always shown — it's free for every authenticated user.
 *
 * Action: clicking "Update" calls GDBCore.InstallUpdate({ productId }),
 * which in C++ dispatches to FGDB_UpdateInstaller (core) or
 * FGDB_ModuleInstaller (module). On failure a toast surfaces the error
 * instead of dying silently.
 *
 * Release-vs-fix filtering happens server-side: only rows in the `versions`
 * table with is_active = 1 surface via /api/hub/versions/check. CI pipeline
 * writes those rows; dev "fix:" / "wip:" commits never reach CI.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/utils/cn'
import { useBridge } from '@/core/bridge/useBridge'
import { useAuth } from '@/lib/auth'
import type { MenuPlacement } from './UserMenu'
import {
  type UpdatePlan,
  type UpdatePlanEntry,
  parseUpdatePlanResponse,
  summarizePlan,
  isEmptyPlan,
} from '@/lib/updates/updatePlanApi'
import { sendTelemetryEvent } from '@/lib/telemetry'
import { getInstalledModules, installModule } from '@/lib/modules/modulesApi'
import { type AvailableUpdate, parseAvailableUpdates } from '@/lib/modules'

// Re-export for back-compat: AvailableUpdate moved to @/lib/modules (GitLab
// #312) so the source-tier fields + Zod parser live with the other module
// download types. Existing imports from this module keep working.
export type { AvailableUpdate }

/** GameDevBuddy core plugin is free for everyone — always shown. */
const CORE_PRODUCT_ID = 'gamedevbuddy'

/**
 * Placeholder version the bell registers for owned-but-NOT-installed modules
 * (so the cloud check reports them "available to install" — see registerOwned
 * below). An entry carrying this `currentVersion` is therefore a fresh INSTALL,
 * not an update: it must go through the sidecar install/stage path (same as the
 * dashboard's Activate), NOT the C++ update path — `GDBCore.InstallUpdate` →
 * `ComposeUpdatePlan` scans LOADED plugins and fails for a module that isn't
 * mounted ("Could not compose plan… plugin may not be loaded").
 */
const FRESH_INSTALL_VERSION = '0.0.0'

/** A bell entry is a fresh install (not an update) when its current version is
 *  the placeholder the bell registered for a not-yet-installed owned module. */
function isFreshInstall(update: AvailableUpdate): boolean {
  return update.currentVersion === FRESH_INSTALL_VERSION
}

/**
 * Dropdown anchoring (shares MenuPlacement with UserMenu). HeaderBar
 * (top-right) opens DOWN-RIGHT; the ActivityBar bottom cluster
 * (bottom-left) passes 'up-right' so the panel opens UP and to the RIGHT
 * into the content area instead of off-screen.
 */
const PLACEMENT_CLASSES: Record<MenuPlacement, string> = {
  'down-right': 'right-0 top-full mt-1',
  'up-right': 'left-full bottom-0 ml-1',
}

interface NotificationBellProps {
  placement?: MenuPlacement
}

/** Same JWT key used by api-client / authApi. Telemetry token source. */
const TOKEN_KEY = 'gdb_token'
function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function NotificationBell({ placement = 'down-right' }: NotificationBellProps = {}) {
  const [isOpen, setIsOpen] = useState(false)
  const [updates, setUpdates] = useState<AvailableUpdate[]>([])
  const [installing, setInstalling] = useState<string | null>(null)
  // Phase 17.7-05 — unified plan state + per-plan dismiss set (D-08).
  const [plan, setPlan] = useState<UpdatePlan | null>(null)
  const [planExpanded, setPlanExpanded] = useState(false)
  const [dismissedBatches, setDismissedBatches] = useState<Set<string>>(
    () => new Set(),
  )
  const [applying, setApplying] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { isConnected, subscribe, callFunction } = useBridge()
  const { modules } = useAuth()

  const closeMenu = useCallback(() => setIsOpen(false), [])

  // Owned module ids — entitlement filter set. Core is always implicitly owned.
  const ownedIds = new Set<string>([CORE_PRODUCT_ID])
  for (const m of modules ?? []) {
    if (m.hasAccess) ownedIds.add(m.id)
  }

  // Visible updates: the plugin-level row (always), or a module the user owns.
  // `isCore` marks the plugin-level row regardless of its product id — the
  // Store's 'gamedevbuddy' AND a standalone bundle's own product id both set
  // it — so a standalone's self-update surfaces even though its id isn't
  // 'gamedevbuddy' and isn't in the entitlement list. Fall back to the legacy
  // id check for an older C++ side that doesn't emit isCore.
  const visibleUpdates = updates.filter(
    (u) => u.isCore || u.productId === CORE_PRODUCT_ID || ownedIds.has(u.productId),
  )

  // Phase 17.7-05 — plan-summary row takes precedence over legacy rows when
  // plan is non-empty AND not dismissed (D-08).
  const planVisible =
    plan !== null && !isEmptyPlan(plan) && !dismissedBatches.has(plan.batchId)
  const hasUpdates = planVisible || visibleUpdates.length > 0

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, closeMenu])

  // Hydrate on mount + whenever the bridge reconnects. Issue #110 split:
  //
  //   - C++ startup (ScanAndRegisterInstalledPaidModules) registers every
  //     INSTALLED sibling plugin with its real .uplugin VersionName via
  //     IPluginManager. Those checkers are authoritative.
  //   - This React loop only needs to fire RegisterOwnedModule for OWNED
  //     modules the user has NOT yet installed, so the cloud check can
  //     report them as "available to install" (placeholder version 0.0.0
  //     makes any published version look newer, which is the intent).
  //
  // We ask the sidecar which modules are installed first, then skip those
  // in the registration loop. The C++ side has a safety net (#110 fix in
  // GDB_CoreModule.cpp:1147) that ignores a 0.0.0 placeholder when a
  // checker is already registered with a real version, but that's a
  // backstop — the cleaner path is for React to not send bogus values in
  // the first place.
  useEffect(() => {
    if (!isConnected) return
    let cancelled = false

    const registerOwned = async () => {
      let installedIds = new Set<string>()
      try {
        const { modules: installed } = await getInstalledModules()
        installedIds = new Set(installed.filter((m) => m.installed).map((m) => m.moduleId))
      } catch (err) {
        // Sidecar unreachable — fall through to the C++ safety net by
        // registering everything with 0.0.0 as before.
        console.warn('[NotificationBell] getInstalledModules failed; falling back to register-all:', err)
      }
      if (cancelled) return

      for (const m of modules ?? []) {
        if (!m.hasAccess || m.id === CORE_PRODUCT_ID) continue
        if (installedIds.has(m.id)) continue // C++ scan handles installed modules
        try {
          await callFunction('GDBCore.RegisterOwnedModule', {
            productId: m.id,
            displayName: m.name,
            currentVersion: '0.0.0',
          })
        } catch (err) {
          console.warn('[NotificationBell] RegisterOwnedModule failed for', m.id, err)
        }
      }
    }

    const fetchLegacyUpdates = async (): Promise<void> => {
      try {
        const raw = await callFunction('GDBCore.GetAvailableUpdates')
        if (cancelled) return
        setUpdates(parseAvailableUpdates(raw))
      } catch (err) {
        console.warn('[NotificationBell] GetAvailableUpdates failed:', err)
      }
    }

    // Phase 17.7-05 — also fetch the unified plan. Wrapped in try/catch so
    // an older Core without the UFUNCTION doesn't break the bell.
    const fetchPlan = async (): Promise<void> => {
      try {
        const raw = await callFunction('GDBCore.GetAvailableUpdatePlan')
        if (cancelled) return
        const parsed = parseUpdatePlanResponse(raw)
        setPlan(parsed)
      } catch (err) {
        console.warn('[NotificationBell] GetAvailableUpdatePlan failed:', err)
      }
    }

    registerOwned()
      .then(() => Promise.all([fetchLegacyUpdates(), fetchPlan()]))
      .catch((err: unknown) => {
        console.warn('[NotificationBell] hydrate failed:', err)
      })

    return () => {
      cancelled = true
    }
    // Re-run when the bridge reconnects or the owned-module set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, callFunction, modules?.length])

  // Subscribe to push refreshes from C++
  useEffect(() => {
    if (!isConnected) return
    const unsub = subscribe('updates.available', (data: unknown) => {
      setUpdates(parseAvailableUpdates(data))
    })
    return unsub
  }, [isConnected, subscribe])

  const handleInstall = useCallback(
    async (update: AvailableUpdate) => {
      setInstalling(update.productId)
      try {
        if (isFreshInstall(update)) {
          // Not installed yet → download + stage via the sidecar (the same
          // path the dashboard Activate uses). The C++ update path would scan
          // loaded plugins and fail because this module isn't mounted.
          const status = await installModule(update.productId)
          if (status.requiresRestart) {
            toast.success('Installed — restart Unreal Editor to finish.', {
              duration: Infinity,
            })
          } else {
            toast.success(`${update.name} installed`)
          }
        } else {
          await callFunction('GDBCore.InstallUpdate', {
            productId: update.productId,
          })
          toast.success(`Installing ${update.name} v${update.latestVersion}…`)
        }
        // Remove from list — C++ will re-push via updates.available if it fails
        setUpdates((prev) => prev.filter((u) => u.productId !== update.productId))
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Install failed'
        toast.error(`Failed to install ${update.name}: ${message}`)
      } finally {
        setInstalling(null)
      }
    },
    [callFunction],
  )

  // Phase 17.7-05 — Apply the whole plan as a batch (D-08, D-03).
  // On success the C++ side fires a persistent Slate "Restart Now" toast
  // (see FGDB_CoreModule::ShowPendingUpdateRestartToast) — that's the
  // actionable UX. We just close the bell here; no redundant React toast.
  const handleApplyPlan = useCallback(async () => {
    if (!plan) return
    setApplying(true)
    try {
      await callFunction('GDBCore.ApplyUpdatePlan')
      closeMenu()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Apply failed'
      toast.error(`Failed to apply update plan: ${message}`)
    } finally {
      setApplying(false)
    }
  }, [plan, callFunction, closeMenu])

  // Phase 17.7-05 — Dismiss is per-plan (D-08): adds batchId to local set +
  // fires plan_dismissed_by_user telemetry. Closes the bell.
  const handleDismissPlan = useCallback(() => {
    if (!plan) return
    const batchId = plan.batchId
    setDismissedBatches((prev) => {
      const next = new Set(prev)
      next.add(batchId)
      return next
    })
    sendTelemetryEvent(
      {
        level: 'info',
        message: 'Update plan dismissed by user',
        source: 'notification-bell',
        context: {
          event: 'plan_dismissed_by_user',
          batchId,
          coreUpgrade: plan.coreUpgrade?.productId ?? null,
          siblingCount: plan.siblingUpgrades.length,
        },
      },
      getStoredToken,
    )
    closeMenu()
  }, [plan, closeMenu])

  // Helper: per-plugin breakdown rows for the expanded panel.
  const planEntries: UpdatePlanEntry[] = plan
    ? (plan.coreUpgrade ? [plan.coreUpgrade] : []).concat(plan.siblingUpgrades)
    : []

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative flex items-center justify-center w-8 h-8 rounded hover:bg-ue-bg-hover transition-colors text-ue-text-tertiary hover:text-ue-text-secondary cursor-pointer"
        title="Notifications"
      >
        <Bell className="size-4" />
        {hasUpdates && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-ue-accent-primary animate-pulse"
          />
        )}
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute w-80 rounded border border-ue-border-medium bg-ue-bg-darker shadow-lg z-50',
            PLACEMENT_CLASSES[placement],
          )}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-ue-border-medium">
            <span className="text-xs font-medium text-ue-text-tertiary">
              Notifications
            </span>
          </div>

          {/* Phase 17.7-05 — plan-summary row takes precedence (D-08). */}
          {planVisible && plan && (
            <div className="border-b border-ue-border-medium">
              <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-ue-bg-hover"
                onClick={() => setPlanExpanded((v) => !v)}
              >
                <span className="text-xs font-medium text-ue-text-primary">
                  {summarizePlan(plan)}
                </span>
                <span className="ml-2 text-[10px] text-ue-text-tertiary shrink-0">
                  {planExpanded ? 'Hide details' : 'Show details'}
                </span>
              </button>
              {planExpanded && (
                <ul className="px-3 pb-2 text-[11px] text-ue-text-secondary leading-snug">
                  {planEntries.map((e) => (
                    <li key={e.productId}>
                      {e.productId}: {e.currentVersion} → {e.targetVersion}
                      {e.minCoreVersion ? ` (min Core ${e.minCoreVersion})` : ''}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={handleDismissPlan}
                  className="px-2 py-0.5 rounded text-[11px] font-medium border border-ue-border-medium text-ue-text-secondary hover:bg-ue-bg-hover transition-colors"
                >
                  Dismiss
                </button>
                <button
                  type="button"
                  onClick={handleApplyPlan}
                  disabled={applying}
                  className={cn(
                    'px-2 py-0.5 rounded text-[11px] font-medium transition-colors',
                    applying
                      ? 'bg-ue-bg-hover text-ue-text-tertiary cursor-wait'
                      : 'bg-ue-accent-primary text-ue-bg-darkest hover:bg-ue-accent-primary/80',
                  )}
                >
                  {applying ? 'Applying…' : 'Apply plan'}
                </button>
              </div>
            </div>
          )}

          {/* Legacy per-plugin rows — fall back when plan empty/dismissed (D-08). */}
          {!planVisible && visibleUpdates.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-ue-text-tertiary">
              No notifications
            </div>
          ) : !planVisible ? (
            <div className="max-h-80 overflow-y-auto">
              {visibleUpdates.map((u) => {
                const isInstalling = installing === u.productId
                const fresh = isFreshInstall(u)
                return (
                  <div
                    key={u.productId}
                    className="px-3 py-2 border-b border-ue-border-medium last:border-b-0"
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-ue-accent-primary" />
                      <span className="text-xs font-medium text-ue-text-primary truncate">
                        {u.name}
                      </span>
                      <span className="text-[10px] text-ue-text-tertiary ml-auto shrink-0">
                        {fresh
                          ? `v${u.latestVersion}`
                          : `v${u.currentVersion} → v${u.latestVersion}`}
                      </span>
                    </div>
                    {u.changelog && (
                      <p className="text-[11px] text-ue-text-secondary leading-snug mb-2 line-clamp-3">
                        {u.changelog}
                      </p>
                    )}
                    <div className="flex justify-end">
                      {u.isSourceTier ? (
                        // GitLab #312 — a source build must never be
                        // auto-swapped (a compiled bundle would clobber the
                        // source checkout). Notify-only: send the user to the
                        // account downloads deep-link to re-download source.
                        // The host is resolved C++-side from gdb.ini/env.
                        <a
                          href={u.accountDownloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 rounded text-[11px] font-medium transition-colors bg-ue-accent-primary text-ue-bg-darkest hover:bg-ue-accent-primary/80"
                        >
                          Re-download source
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleInstall(u)}
                          disabled={isInstalling}
                          className={cn(
                            'px-2 py-0.5 rounded text-[11px] font-medium transition-colors',
                            isInstalling
                              ? 'bg-ue-bg-hover text-ue-text-tertiary cursor-wait'
                              : 'bg-ue-accent-primary text-ue-bg-darkest hover:bg-ue-accent-primary/80',
                          )}
                        >
                          {isInstalling ? 'Installing…' : fresh ? 'Install' : 'Update'}
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
